import { useState, useEffect } from "react";
import {
  Button,
  Tooltip,
} from "@mui/material";
import { URLS } from "../../helpers/URLS";
import useAxios from "../../helpers/Hooks/useAxios";
import SkeletonLoader from "../../loaders/Skeleton";
import NoData from "../../loaders/NoData";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  TableSortLabel,
  IconButton,
  Box,
  Typography,
  Toolbar
} from "@mui/material";
import { Edit, Delete, Visibility, Add, UploadFile } from "@mui/icons-material";
import AddEmployeeForm from "../../components/AddEmployee/AddEmployee";
import BulkUploadModal from "./BulkUploadModal";
import EmployeeDetail from "./EmployeeDetail";
import EmployeeEdit from "./EmployeeEdit";
import ConfirmRemove from "./ConfirmRemove";
import { toast } from "react-toastify";
import "../../assets/scss/tables.scss";
import { downloadLink } from "../../helpers/constFIles";

// Sorting functions
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, comparator) {
  return array.slice().sort(comparator);
}

const initials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0] ? parts[0][0] : "") +
    (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

const columns = [
  { id: "employeeCode", label: "ID" },
  { id: "employeeName", label: "Name" },
  { id: "emailID", label: "Email" },
  { id: "department", label: "Department" },
  { id: "designation", label: "Designation" },
  { id: "managerName", label: "Manager Name" },
  { id: "action", label: "Action", sortable: false },
];

const ManageEmployees = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const { fetchData } = useAxios();
  const [employeeList, setemployeeList] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [loadSkeleton, setLoadSkeleton] = useState(true);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("id");
  const [rows, setRows] = useState([]);
  const [editEmployeeData, setEditEmployeeData] = useState(null);
  const [openAddEmployee, setOpenAddEmployee] = useState(false);
  const [openBulkUpload, setOpenBulkUpload] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    getEmployeeList();
  }, []);

  const saveEmployee = async (payload) => {
    const resp = await fetchData({
      url: URLS.updateEmployee,
      method: "put",
      body: payload,
    });

    if (resp?.status === 200) {
      toast.success(resp.message || "Employee updated");
      setEditing(null);
      getEmployeeList();
      return true;
    }
    toast.error(resp?.message || "Could not update the employee");
    return false;
  };

  const removeEmployee = async () => {
    const resp = await fetchData({
      url: `${URLS.deleteEmployee}?employeeCode=${encodeURIComponent(removing.employeeCode)}`,
      method: "delete",
    });

    if (resp?.status === 200) {
      toast.success(resp.message || "Employee removed");
      setRemoving(null);
      getEmployeeList();
    } else {
      toast.error(resp?.message || "Could not remove the employee");
    }
  };

  const getEmployeeList = async () => {
    setLoadSkeleton(true);
    const resp = await fetchData({ url: URLS.getEmployees, method: "get" });
    if (resp.status === 200) {
      setemployeeList(resp.data);
      setLoadSkeleton(false);
      setRows(resp.data);
      console.log(resp.data);
    }
  };

  const handleAddOpen = () => {
    setEditEmployeeData(null);
    setOpenAddEmployee(true);
  };

  const handleEditOpen = (row) => {
    setEditEmployeeData(row);
    setOpenAddEmployee(true);
  };

  const handleClose = () => {
    setOpenAddEmployee(false);
    setEditEmployeeData(null);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const filteredRows = rows.filter((row) =>
    Object.values(row).some((val) =>
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const sortedRows = stableSort(filteredRows, getComparator(order, orderBy));
  const paginatedRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box className="wh-table" sx={{ padding: "20px" }}>
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          marginBottom: "20px",
          paddingLeft: "0px"
        }}
      >
        <Typography variant="h5" className="wh-title" sx={{ marginBottom: "16px" }}>
          Manage Employees
        </Typography>

        {/* Search Bar */}

        <TextField
          label="Search Employee"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={handleSearch}
          size="small"
          sx={{ maxWidth: "400px" }}
        />
        <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            onClick={() => setOpenBulkUpload(true)}
            sx={{
              borderColor: "#2b8fe0",
              color: "#1c4f86",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              padding: "7px 16px",
              "&:hover": { borderColor: "#1c4f86", backgroundColor: "#f0f7ff" }
            }}
          >
            Bulk Upload
          </Button>
          <Button
            variant="contained"
            className="wh-primary-btn"
            startIcon={<Add />}
            onClick={handleAddOpen}
          >
            Add Employee
          </Button>
        </Box>
      </Toolbar>
      <Box>
        <AddEmployeeForm open={openAddEmployee} onClose={handleClose} editData={editEmployeeData} onSuccess={getEmployeeList} />
        <BulkUploadModal open={openBulkUpload} onClose={() => setOpenBulkUpload(false)} onSuccess={getEmployeeList} />
      </Box>

      <EmployeeDetail
        employee={viewing}
        onClose={() => setViewing(null)}
        onEdit={(emp) => { setViewing(null); setEditing(emp); }}
      />

      <EmployeeEdit
        employee={editing}
        onClose={() => setEditing(null)}
        onSave={saveEmployee}
      />

      <ConfirmRemove
        employee={removing}
        onClose={() => setRemoving(null)}
        onConfirm={removeEmployee}
      />

      {loadSkeleton ? (
        <SkeletonLoader />
      ) : employeeList.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table>
              {/* Table Head */}
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={column.id === "action"
                        ? "sticky_right_header_column wh-actions-3" : undefined}
                      sortDirection={orderBy === column.id ? order : false}
                    >
                      {column.sortable !== false ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={() => handleRequestSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              {/* Table Body */}
              <TableBody>
                {paginatedRows.map((row) => (
                  <TableRow key={row.employeeCode}>
                    <TableCell>{row.employeeCode}</TableCell>
                    <TableCell>
                      <div className="wh-person">
                        {row.profilePic
                          ? <img className="wh-avatar wh-avatar-sm"
                              src={downloadLink + row.profilePic}
                              alt=""
                              /* a deleted file falls back to the initials */
                              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          : null}
                        <span className="wh-avatar wh-avatar-sm">{initials(row.employeeName)}</span>
                        <button type="button" className="wh-person-name wh-person-link"
                          title={`View ${row.employeeName}`}
                          onClick={() => setViewing(row)}>
                          {row.employeeName}
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>{row.emailID}</TableCell>
                    <TableCell>{row.department}</TableCell>
                    <TableCell>{row.designation}</TableCell>
                    <TableCell>{row.managerName}</TableCell>
                    <TableCell className="sticky_right_column wh-actions-3">
                      <div className="wh-actions-row">
                        <Tooltip title="View employee" placement="top" arrow>
                          <IconButton onClick={() => setViewing(row)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit employee" placement="top" arrow>
                          <IconButton onClick={() => setEditing(row)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Remove employee" placement="top" arrow>
                          <IconButton
                            className="wh-danger"
                            onClick={() => setRemoving(row)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {/* No Data Found */}
                {paginatedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No matching records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[15, 25, 50]}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      ) : (
        <NoData />
      )}
    </Box>
  );
};

export default ManageEmployees;
